import {
  LitElement,
  html,
  css,
  unsafeHTML,
  paths,
  ref,
  createRef,
  classMap,
  t,
  utils,
} from '@hh/global.js';
const { debounce } = utils

class ScrollableBox extends LitElement {
  static properties = {
    _isDown: false,
    _items: [],
    _momentumID: false,
    _scrollLeft: false,
    _showNext: false,
    _showPrev: false,
    _startX: false,
    _velX: 0,
    transformButtonsOnScroll: {
      type: Boolean,
    },
    hideScrollbar: {
      type: Boolean,
      attribute: 'hide-scrollbar',
    },
    featureItem: {
      type: Boolean,
      attribute: 'feature-item',
    },
    featureClass: {
      type: String,
      attribute: 'feature-class',
    },
    _thumbDragging: { state: true },
  }

  $controls = createRef()
  $nextButton = createRef()
  $prevButton = createRef()
  $slider = createRef()
  $container = createRef()
  $scrollbarTrack = createRef()
  $scrollbarThumb = createRef()
  _scrollingTimer = null
  _scrollbarRAF = false

  constructor() {
    super()
    this.render()
    this._thumbDragging = false
    this.onThumbDown = this.onThumbDown?.bind(this)
    this.onThumbMove = this.onThumbMove?.bind(this)
    this.onThumbUp = this.onThumbUp?.bind(this)
    this.onTrackClick = this.onTrackClick?.bind(this)
    this.updateScrollbar = this.updateScrollbar?.bind(this)
    this.onScrollBarScroll = this.onScrollBarScroll?.bind(this)
  }

  scrollCarousel(num) {
    const slider = this.$slider?.value
    if (!slider) {
      return false
    }
    return (slider.scrollLeft += num)
  }

  shouldShowPrev() {
    const items = this._items
    if (!items || items.length === 0) {
      return false
    }

    const slider = this.$slider?.value
    if (!slider) {
      return false
    }

    const minimumScroll = 0
    const currentScroll = slider.scrollLeft
    return currentScroll > minimumScroll
  }

  shouldShowNext() {
    const items = this._items
    if (!items || items.length === 0) {
      return false
    }

    const slider = this.$slider?.value
    if (!slider) {
      return false
    }

    const columnSeparation = parseInt(
      getComputedStyle(slider)?.columnGap?.replace('px', '') || 0
    )
    const itemsWidth =
      items.reduce((acc, item) => item.clientWidth + acc, 0) +
      columnSeparation * (items.length - 1)

    if (itemsWidth < 1) {
      return false
    }

    if (this.classList.contains('js-teaser-carousel') && slider.scrollLeft === 0) {
      return this.isTeaserOverflowing(slider)
    } else {
      return (
        Math.ceil(slider.scrollLeft + slider.offsetWidth) < slider.scrollWidth
      )
    }
  }

  isTeaserOverflowing(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.left < 0 ||
      rect.right > (window.innerWidth || this.querySelector(element).clientWidth)
    );
  }

  getItems() {
    const slot = this.renderRoot?.querySelector('slot')
    if (!slot) {
      return false
    }

    return slot.assignedElements({ flatten: true })
  }

  updateButtonsVisibility() {
    const items = this._items
    if (!items || items.length === 0) {
      this._showPrev = false
      this._showNext = false
      return
    }

    setTimeout(() => {
      this._showPrev = this.shouldShowPrev()
      this._showNext = this.shouldShowNext()
      this.updateScrollableClass()
    }, 100)
  }

  updateScrollableClass() {
    if (this._showPrev || this._showNext) {
      this.classList.add('is-scrollable')
    } else {
      this.classList.remove('is-scrollable')
    }
  }

  transformControls() {
    const controls = this.$controls?.value
    if (!controls) {
      return
    }

    if (!this.transformButtonsOnScroll) {
      return
    }

    controls.classList.add('scrollable-box__controls--active')
  }

  firstUpdated() {
    this._items = this.getItems()

    setTimeout(() => {
      this.updateButtonsVisibility()
    }, 100)

    const slider = this.$slider.value
    let pointerDown = false

    if (!slider) {
      return
    }

    this.updateHorizontalAlign()

    this.addEventListener('mousedown', () => {
      pointerDown = true
    })

    this.addEventListener('mouseup', () => {
      pointerDown = false
    })

    this.addEventListener('mousemove', () => {
      if (pointerDown) {
        return
      }
      for (var i = 0; i < this._items.length; i++) {
        this._items[i].classList.remove('no-pointer-events')
      }
    })

    setTimeout(() => {
      const eventName = 'onscrollend' in window ? 'scrollend' : 'scroll'
      slider.addEventListener(eventName, () => {
        this.updateButtonsVisibility()
        if (slider.classList.contains('scrollable-box__content')) {
          this.transformControls()
        }

        if ('onscrollend' in window) {
          this.setFeaturedItemOnScroll()
        } else {
          debounce(() => {
            this.setFeaturedItemOnScroll()
          }, 250)()
        }
        this.updateScrollbar && this.updateScrollbar()
      })
      // Always update scrollbar on scroll for smoothness
      slider.addEventListener('scroll', this.onScrollBarScroll, { passive: true })
    }, 350)

    slider.addEventListener('resize', () => {
      if (window.innerWidth > 1023) {
        slider.scrollTo({
          left: 0,
        })
      }

      this.updateButtonsVisibility()
      debounce(() => {
        this.updateHorizontalAlign()
        this.updateScrollbar && this.updateScrollbar()
      }, 250)()
    })

    slider.addEventListener('mousedown', (e) => {
      this._isDown = true
      this._startX = e.pageX - slider.offsetLeft
      this._scrollLeft = slider.scrollLeft
    })

    slider.addEventListener('mouseleave', () => {
      this._isDown = false
    })

    slider.addEventListener('mouseup', () => {
      this._isDown = false
    })

    slider.addEventListener('mousemove', (e) => {
      if (!this._isDown) {
        return
      }
      e.preventDefault()
      const x = e.pageX - slider.offsetLeft
      const walk = x - this._startX
      const prevScrollLeft = slider.scrollLeft
      slider.scrollLeft = this._scrollLeft - walk
      this._velX = slider.scrollLeft - prevScrollLeft
    })

    // custom scrollbar events
    const track = this.$scrollbarTrack?.value
    const thumb = this.$scrollbarThumb?.value
    if (track && thumb) {
      track.addEventListener('click', this.onTrackClick)
      thumb.addEventListener('mousedown', this.onThumbDown)
      window.addEventListener('mouseup', this.onThumbUp)
      window.addEventListener('mousemove', this.onThumbMove)
    }

    setTimeout(() => this.updateScrollbar && this.updateScrollbar(), 200)
  }

  updateHorizontalAlign() {
    const slider = this.$slider.value
    if (!slider) {
      return
    }

    if (this.featureItem) {
      this.setFeaturedItemClass(this._items[0])
    }
  }

  setFeaturedItemClass(item) {
    if (item.classList.contains(this.featureClass)) {
      return
    }

    this._items.forEach((_item) => {
      _item.classList.remove(this.featureClass)
    })
    item.classList.add(this.featureClass)
  }

  setFeaturedItemOnScroll() {
    if (!this.featureItem) {
      return
    }

    const slider = this.$slider.value
    if (!slider) {
      return
    }

    const currentScroll = slider.scrollLeft
    const columnSeparation = parseInt(
      getComputedStyle(slider)?.columnGap?.replace('px', '') || 0
    )
    const foundIndex = this._items.reduce((acc, item, index) => {
      return item.classList.contains(this.featureClass) ? index : acc
    }, -1)
    const featuredItem =
      foundIndex > -1 ? this._items[foundIndex] : this._items[0]
    const scrollAmount = featuredItem.clientWidth - columnSeparation * 3 - 2
    let index =
      currentScroll < scrollAmount / 3
        ? 0
        : Math.floor(currentScroll / scrollAmount)
    if (isNaN(index) || index < 0 || typeof index !== 'number') {
      return
    }

    if (index >= this._items.length) {
      index = this._items.length - 1
    }
    this.setFeaturedItemClass(this._items[index])
  }

  move(ev) {
    ev.preventDefault()
    const slider = this.$slider?.value
    if (!slider) {
      return
    }

    const items = this._items
    if (!items || items.length === 0) {
      return
    }

    const columnSeparation = parseInt(
      getComputedStyle(slider)?.columnGap?.replace('px', '') || 0
    )
    const scrollAmount = items[0].clientWidth + columnSeparation
    const slideScrollPosition =
      ev.currentTarget.name === 'next'
        ? slider.scrollLeft + scrollAmount
        : slider.scrollLeft - scrollAmount

    slider.scrollTo({
      left: slideScrollPosition,
      behavior: 'smooth',
    })
  }

  // Custom scrollbar helpers
  updateScrollbar() {
    const slider = this.$slider?.value
    const track = this.$scrollbarTrack?.value
    const thumb = this.$scrollbarThumb?.value
    if (!slider || !track || !thumb) return

    const total = slider.scrollWidth
    const visible = slider.clientWidth
    const maxScroll = Math.max(1, total - visible)
    const trackWidth = track.clientWidth
    const minThumb = 30
    const thumbWidth = Math.max((visible / total) * trackWidth, minThumb)
    const left = (slider.scrollLeft / maxScroll) * (trackWidth - thumbWidth)

    thumb.style.width = `${thumbWidth}px`
    thumb.style.transform = `translateX(${Math.max(0, Math.min(left, trackWidth - thumbWidth))}px)`
  }

  onTrackClick(e) {
    const slider = this.$slider?.value
    const track = this.$scrollbarTrack?.value
    const thumb = this.$scrollbarThumb?.value
    if (!slider || !track || !thumb) return

    const rect = track.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const target = Math.max(0, Math.min(track.clientWidth, clickX))
    const max = Math.max(1, track.clientWidth - thumb.clientWidth)
    const ratio = Math.max(0, Math.min(1, (target - thumb.clientWidth / 2) / max))
    const maxScroll = slider.scrollWidth - slider.clientWidth
    slider.scrollLeft = ratio * maxScroll
    this.updateScrollbar()
  }

  onThumbDown(e) {
    e.preventDefault()
    this._thumbDragging = true
    document.body.classList.add('scrollable-box--dragging')
  }

  onThumbUp() {
    if (!this._thumbDragging) return
    this._thumbDragging = false
    document.body.classList.remove('scrollable-box--dragging')
  }

  onThumbMove(e) {
    if (!this._thumbDragging) return
    const slider = this.$slider?.value
    const track = this.$scrollbarTrack?.value
    const thumb = this.$scrollbarThumb?.value
    if (!slider || !track || !thumb) return

    const rect = track.getBoundingClientRect()
    const x = e.clientX - rect.left
    const max = Math.max(1, track.clientWidth - thumb.clientWidth)
    const clamped = Math.max(0, Math.min(max, x - thumb.clientWidth / 2))
    const ratio = clamped / max
    const maxScroll = slider.scrollWidth - slider.clientWidth
    slider.scrollLeft = ratio * maxScroll
    this.updateScrollbar()
  }

  onScrollBarScroll() {
    if (this._scrollbarRAF) return
    this._scrollbarRAF = true
    requestAnimationFrame(() => {
      this.updateScrollbar()
      this._scrollbarRAF = false
    })
  }

  render() {
    return html`<div class="scrollable-box__container" ${ref(this.$container)}>
        <ul class="scrollable-box__content" ${ref(this.$slider)}>
          <slot name="item"></slot>
          <li class="scrollable-box__extra-item"></li>
        </ul>
      </div>
      <div
        class="scrollable-box__controls${classMap({
          'scrollable-box__controls--hidden': !(
            this._showPrev || this._showNext
          ),
        })}"
        ${ref(this.$controls)}
      >
       <div class="scrollable-box__controls-buttons">
         <button
          type="button"
          class="scrollable-box__button scrollable-box__button--prev${classMap({
            'scrollable-box__button--visible': this._showPrev,
          })}"
          ${ref(this.$prevButton)}
          name="previous"
          @click="${this.move}"
          ?disabled="${!this._showPrev}"
          aria-label="${t('accessibility.previous_slide')}"
        >
          ${unsafeHTML(paths.arrowPoint)} ${unsafeHTML(paths.chevron)}
        </button>
        <button
          type="button"
          class="scrollable-box__button scrollable-box__button--next${classMap({
            'scrollable-box__button--visible': this._showNext,
          })}"
          ${ref(this.$nextButton)}
          name="next"
          @click="${this.move}"
          ?disabled="${!this._showNext}"
          aria-label="${t('accessibility.next_slide')}"
        >
          ${unsafeHTML(paths.arrowPoint)} ${unsafeHTML(paths.chevron)}
        </button>
       </div>
        <div class="scrollable-box__scrollbar">
          <div class="scrollable-box__scrollbar-track" ${ref(this.$scrollbarTrack)}>
            <div class="scrollable-box__scrollbar-thumb" ${ref(this.$scrollbarThumb)}></div>
          </div>
        </div>
      </div>`
  }

  static styles = css`
    :host {
      overflow: visible;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host ::slotted(*) {
      list-style-type: none;
    }

    :host ::slotted(.no-pointer-events) {
      pointer-events: none;
    }

    .scrollable-box__content::-webkit-scrollbar {
      display: none;
    }

    .scrollable-box__content {
      -ms-overflow-style: none;
      -webkit-overflow-scrolling: touch;
      align-items: var(--hh-v-align-items, stretch);
      display: var(--hh-box-display, grid);
      column-gap: var(--hh-column-gap, var(--hh-spacing-md, 1.6rem));
      grid-auto-flow: column;
      justify-content: start;
      list-style: none;
      margin: 0;
      min-height: var(--hh-min-height, auto);
      min-width: var(--hh-min-width, 100vw);
      overflow-x: auto;
      padding: 0;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;
      scroll-padding-left: var(--hh-column-gap, var(--hh-spacing-md, 1.6rem));
      scrollbar-width: none;
    }

    ::slotted([slot='item'] a) {
      pointer-events: none;
    }

    .scrollable-box__extra-item {
      margin: 0;
      padding: 0;
    }

    @supports (-webkit-touch-callout: none) {
      .scrollable-box__extra-item {
        height: 1px;
        padding-right: 1px;
      }
    }

    .scrollable-box__controls {
      margin-top: var(--hh-spacing-2xl, 32px);
    }

    .scrollable-box__button {
      background-color: var(--hh-button-bg, var(--hh-color-day, #fff));
      border-width: 0;
      border-radius: var(--hh-spacing-2xl, 32px);
      color: var(--hh-button-fg, var(--hh-color, #646464));
      cursor: pointer;
      display: inline-block;
      font-size: 0;
      line-height: 1;
      opacity: 0;
      padding: 2px 12px;
      pointer-events: none;
      transition: opacity var(--hh-duration-default) ease,
        width var(--hh-duration-default) ease
          calc(var(--hh-duration-default) * 2);
    }

    .scrollable-box__button:disabled {
      cursor: not-allowed;
    }

    .scrollable-box__button--visible {
      opacity: 1;
      pointer-events: all;
    }

    .scrollable-box__button--prev > * {
      transform: rotate(180deg);
    }

    .icon-chevron {
      display: none;
    }

    .icon-arrow-point {
      height: 28px;
      pointer-events: none;
      width: 46px;
    }

    .scrollable-box__button--prev .icon-chevron {
      transform: translate(-55%, -50%) rotate(90deg);
    }

    .scrollable-box__button--next .icon-chevron {
      transform: translate(-45%, -50%) rotate(270deg);
    }

    .scrollable-box__container {
      transition: all 0.8s ease 0s;
      margin: 0;
    }

    .scrollable-box__content--full-centered {
      justify-content: center;
    }
    :host([hide-scrollbar]) .scrollable-box__scrollbar {
      display: none;
    }

    @media screen and (min-width: 1280px) {
      .scrollable-box {
        overflow: visible;
      }

      .scrollable-box__controls {
        margin-top: var(--hh-spacing-3xl, 32px);
      }
    }

    @media screen and (min-width: 1400px) {
      .scrollable-box__controls {
        margin-top: var(--hh-spacing-3xl, 32px);
      }
    }

    :host([stack-cards-on-mobile])
      ::slotted([slot='item'].distribution__carousel-item) {
      margin-bottom: 6rem;
    }

    @media screen and (max-width: 1023px) {
      :host([stack-cards-on-mobile]) .scrollable-box__content {
        width: 100%;
        display: flex;
        overflow: visible;
      }

      :host([stack-cards-on-mobile])
        ::slotted([slot='item'].distribution__carousel-item) {
        scale: 0.9 !important;
        width: 100% !important;
        display: flex !important;
      }
    }

    .scrollable-box__content--centered {
        justify-content: center;
        overflow: visible;
      }

      .scrollable-box__controls {
        column-gap: var(--hh-spacing-md);
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin: var(--hh-spacing-xl, 24px) 0 0;
        max-width: var(--hh-controls-max-width, 100%);
        justify-content: space-between;
        flex-direction: row-reverse;
      }

      .scrollable-box__controls--active {
        display: flex;
        align-items: center;
        gap: 24px;
        text-align: end;
      }

      .scrollable-box__controls--hidden {
        display: none;
      }

      .scrollable-box__controls--active .icon-arrow-point {
        display: none;
      }

      .scrollable-box__controls--active .icon-chevron {
        display: block;
        height: 24px;
        width: 24px;
        position: absolute;
        left: 50%;
        top: 50%;
      }

      .scrollable-box__controls--active .scrollable-box__button {
        opacity: 1;
        padding: var(--hh-spacing-md, 16px);
        position: relative;
      }

      .scrollable-box__controls--active .scrollable-box__controls-buttons {
        opacity: 1;
        display: flex;
        align-items: center;
        gap: 5px;
      }

      .scrollable-box__controls--active .scrollable-box__button:disabled {
        opacity: 0.7;
      }

      .scrollable-box__scrollbar { flex: 1; display: flex; align-items: center; }
      .scrollable-box__scrollbar-track {
        position: relative;
        height: 6px;
        width: 100%;
        background: rgba(0,0,0,0.08);
        border-radius: 6px;
        cursor: pointer;
      }
      .scrollable-box__scrollbar-thumb {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 60px;
        background-color: var(--hh-button-bg, var(--hh-color-day, #fff));
        border-radius: 6px;
        cursor: pointer;
        transition: transform 60ms linear, width 120ms ease;
        box-shadow: 0 1px 2px rgba(0,0,0,0.15);
      }

    @media screen and (max-width: 639px) {
      :host([stack-cards-on-mobile]) .scrollable-box__content {
        display: block;
        overflow: visible;
        margin-bottom: 5rem;
      }

      :host([stack-cards-on-mobile])
        ::slotted([slot='item'].distribution__carousel-item) {
        padding: 0;
        scale: 1 !important;
        transform: none !important;
        margin: 1rem 1.6rem !important;
        width: calc(100% - 3.2rem) !important;
      }

      :host(.generic-distribution)
        ::slotted([slot='item'].distribution__carousel-item) {
        margin: 1.6rem !important;
      }
    }

    @media screen and (min-width: 1900px) {
      .scrollable-box__content--centered {
        justify-content: start;
      }
    }
  `
}

if (!customElements.get('hh-scrollable-box')) {
  customElements.define('hh-scrollable-box', ScrollableBox);
}
